import DOMPurify from 'isomorphic-dompurify'

/** 許可する target の値（小文字）。それ以外（フレーム名など）は落とす */
const ALLOWED_TARGETS = new Set(['_blank', '_self'])

/** target を持てる要素（リンク）。SVG の <a> も localName は 'a' */
const LINK_TAGS = new Set(['a', 'area'])

/**
 * `target` 属性を許可する（DOMPurify の既定では落とされる）。
 * モジュール定数にしているのは、同じ参照を渡すと DOMPurify が設定の再解析を省くため
 * （HtmlContent は描画のたびに sanitizeHtml を呼ぶ）。
 */
const SANITIZE_CONFIG = { ADD_ATTR: ['target'] }

/**
 * target 属性を「リンク要素の _blank / _self」だけに絞り、_blank には rel="noopener noreferrer" を強制する。
 * 管理画面の保存時サニタイザー（app.admin_wysiwyg）と同じ方針（#3609）。
 *
 * ADD_ATTR は要素を問わず target を通すため、リンク以外（form / p など）の target はここで落とす。
 * target のキーワードはブラウザが大文字小文字を区別しないので、小文字に正規化してから判定する。
 *
 * フックはモジュール読み込み時に登録する（DOMPurify のフックはインスタンス全体に効く）。
 * テストランナーがモジュールを再評価すると同じフックが重複登録されるが、処理は冪等なので結果は変わらない。
 */
DOMPurify.addHook('afterSanitizeAttributes', (node) => {
  const rawTarget = node.getAttribute('target')
  if (rawTarget === null) return

  const target = rawTarget.toLowerCase()
  if (!LINK_TAGS.has(node.localName.toLowerCase()) || !ALLOWED_TARGETS.has(target)) {
    node.removeAttribute('target')
    return
  }

  node.setAttribute('target', target)
  if (target === '_blank') {
    node.setAttribute('rel', 'noopener noreferrer')
  }
})

/**
 * APIから取得したHTML文字列をDOMPurifyでサニタイズして返す。
 * `dangerouslySetInnerHTML` で埋め込む前に必ず使用すること。
 * isomorphic-dompurify を使用することでSSR/プリレンダリング環境でも安全にサニタイズされる。
 *
 * - リンク（a / area）の `target` 属性を保持する。管理画面のリンクダイアログで選んだ
 *   「新しいウィンドウ／現在のウィンドウ」をアプリでも尊重するため（#3609）
 * - `target="_blank"` には `rel="noopener noreferrer"` を強制する（上の afterSanitizeAttributes フック）
 * - リンク以外の要素の `target`、`_blank` / `_self` 以外の値は落とす
 */
export const sanitizeHtml = (html: string): string => {
  return DOMPurify.sanitize(html, SANITIZE_CONFIG)
}
